import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { differenceWith } from 'lodash-es';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { lookupsJson, policyType as policyTypes } from '@ourbranch/lookups';

import { useDisabledState } from 'common/disabled-context';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { FormField } from 'core/components/form';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import useStyles from './styles';
import useFeeUpdater from './use-fee-updater';

const feeComparator = (option, fee) => option.id === fee.type;
const feeOptions = lookupsJson.feeOptions;

function FeeSelector({ geographicState, policyType }) {
  const {
    account: {
      policies: {
        list,
        policy: { policy, addFee, fees, policyDetails }
      }
    }
  } = useStore();
  const { canEditFeeCheckbox } = useSession();
  const { updateFees } = useFeeUpdater();
  const { values, setFieldValue } = useFormikContext();
  const classes = useStyles();
  const { disabled } = useDisabledState();

  const createFee = () => {
    let inBundle = false;
    const activePolicies = list.filter((policy) => getPolicyStatus(policy) === PolicyStatus.Active);
    // if this is an auto policy, check to see if there is an active home, renters, or condo policy
    if (policyType === policyTypes.Auto) {
      inBundle =
        activePolicies
          .filter((policy) => policy.policyType !== policyTypes.Auto)
          .map((policy) => policy.policyType)
          .some((type) => ['H', 'R', 'C'].includes(type)) || policyDetails.includeRenters;
      // if this is not an auto policy (home, condo, or renters), check to see if there is an active auto policy on the account
    } else {
      inBundle = activePolicies.filter((policy) => policy.policyType === policyTypes.Auto).length > 0;
    }

    addFee(values.fees, {
      state: geographicState,
      policyType,
      isBix: policy.isBix,
      inBundle
    });
    setFieldValue('fees', undefined);
    updateFees();
  };

  const filteredFees = useMemo(() => {
    // remove options already applied to policy
    const availableFeeOptions = differenceWith(feeOptions, fees, feeComparator);
    let filteredFees = availableFeeOptions;
    // Imagery fee only applies to TX GSNIC Home and MO GSNIC
    if (
      (geographicState === 'TX' && (policy.isBix || (!policy.isBix && policyType !== 'H'))) ||
      (geographicState === 'MO' && policy.isBix)
    ) {
      filteredFees = filteredFees.filter((fee) => !fee.id.includes('imagery'));
    }
    // Paper fee only applies for TX GSNIC policies
    if (geographicState === 'TX' && policy.isBix) {
      filteredFees = filteredFees.filter((fee) => !fee.id.includes('paper'));
    }
    // Policy fee does not apply to MO BIX, TX BIX, or Home policies in AL
    if (
      (geographicState === 'MO' && policy.isBix) ||
      (geographicState === 'TX' && policy.isBix) ||
      (geographicState === 'AL' && policyType === 'H')
    ) {
      filteredFees = filteredFees.filter((fee) => !fee.id.includes('policy'));
    }
    // Reinstatement fee does not apply to auto policies in MI or home policies in SD
    if ((geographicState === 'MI' && policyType === 'A') || (geographicState === 'SD' && policyType === 'H')) {
      filteredFees = filteredFees.filter((fee) => !fee.id.includes('reinstatement'));
    }
    // Auto Theft Prevention Device fee only applies to auto policies
    if (policyType !== 'A') {
      filteredFees = filteredFees.filter((fee) => !fee.id.includes('auto_theft_prevention'));
    }
    return filteredFees;
  }, [fees, policy.isBix, policyType, geographicState]);

  return (
    <Grid className={classes.feeSelectorContainer}>
      <FormField
        className={classes.formControl}
        mode="light"
        name="fees"
        id="fees"
        options={filteredFees}
        type="select"
        xs={8}
        disabled={disabled}
      />
      <Button
        variant="contained"
        color="secondary"
        disabled={!canEditFeeCheckbox || !values.fees?.length || disabled}
        className={classes.button}
        onClick={createFee}
      >
        Add
      </Button>
    </Grid>
  );
}

FeeSelector.propTypes = {
  geographicState: PropTypes.string.isRequired,
  policyType: PropTypes.string.isRequired
};

export default observer(FeeSelector);
